/**
 * src/lib/voice/useVoiceStateMachine.js — Phase 76-02 Task 1
 *
 * Pure voice conversation state machine. Extracted from
 * useVoiceConversation.js so the parent hook stays a thin orchestrator
 * (see 76-02 plan AC-1 / CLAUDE.md 500-line rule).
 *
 * States:
 *   idle | recording | processing | speaking
 *   | awaiting_confirmation | listening_for_confirmation | error
 *
 * Transitions (others are no-ops + dev warning):
 *   idle                        -> recording, processing
 *   recording                   -> processing, error
 *   processing                  -> speaking, awaiting_confirmation, idle, error
 *   speaking                    -> idle, awaiting_confirmation, recording (barge-in)
 *   awaiting_confirmation       -> listening_for_confirmation, idle, error
 *   listening_for_confirmation  -> processing, idle
 *   error                       -> idle (auto after 4s)
 *
 * Time-based side effects owned here (kept minimal on purpose):
 *   - error state auto-resets to idle after 4000 ms
 *   - processing state raises onError('timeout') + moves to error after 50000 ms
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { logVoiceEvent } from './voiceLogger';

export const VOICE_STATES = Object.freeze({
  IDLE: 'idle',
  RECORDING: 'recording',
  PROCESSING: 'processing',
  SPEAKING: 'speaking',
  AWAITING_CONFIRMATION: 'awaiting_confirmation',
  LISTENING_FOR_CONFIRMATION: 'listening_for_confirmation',
  ERROR: 'error',
});

const ALLOWED = Object.freeze({
  // P6 (2026-06-10): added 'error' to idle so a late TTS failure callback
  // (fired after the FSM already returned to idle) lands cleanly instead of
  // triggering voice_state_invalid and wedging the reply.
  idle: ['recording', 'processing', 'error'],
  recording: ['processing', 'error', 'idle'],
  processing: ['speaking', 'awaiting_confirmation', 'idle', 'error'],
  speaking: ['idle', 'awaiting_confirmation', 'recording', 'error'],
  awaiting_confirmation: ['listening_for_confirmation', 'speaking', 'idle', 'error'],
  listening_for_confirmation: ['processing', 'idle', 'error'],
  error: ['idle'],
});

export const ERROR_AUTO_RESET_MS = 4000;
export const PROCESSING_SAFETY_MS = 50000;

function isAllowed(from, to) {
  if (from === to) return true;
  const next = ALLOWED[from];
  return Array.isArray(next) && next.includes(to);
}

export default function useVoiceStateMachine({
  onEnterIdle,
  onEnterRecording,
  onEnterProcessing,
  onEnterSpeaking,
  onEnterAwaitingConfirmation,
  onEnterListeningForConfirmation,
  onError,
  errorAutoResetMs = ERROR_AUTO_RESET_MS,
  processingSafetyMs = PROCESSING_SAFETY_MS,
} = {}) {
  const [state, setStateRaw] = useState('idle');

  const callbacksRef = useRef({});
  callbacksRef.current = {
    onEnterIdle,
    onEnterRecording,
    onEnterProcessing,
    onEnterSpeaking,
    onEnterAwaitingConfirmation,
    onEnterListeningForConfirmation,
    onError,
  };

  const transitionTo = useCallback((next, reason) => {
    let applied = false;
    setStateRaw((prev) => {
      if (prev === next) return prev;
      if (!isAllowed(prev, next)) {
        if (import.meta?.env?.DEV) {
          console.warn(
            `[useVoiceStateMachine] invalid transition ${prev} -> ${next}` +
            (reason ? ` (reason: ${reason})` : ''),
          );
        }
        logVoiceEvent('voice_state_invalid', 'error',
          `blocked ${prev} -> ${next}`,
          { from: prev, to: next, reason: reason || null });
        return prev;
      }
      applied = true;
      return next;
    });
    if (applied) {
      logVoiceEvent('voice_state', 'ok', `${next}`,
        { to: next, reason: reason || null });
    }
    return applied;
  }, []);

  useEffect(() => {
    const cb = callbacksRef.current;
    switch (state) {
      case 'idle': cb.onEnterIdle?.(); break;
      case 'recording': cb.onEnterRecording?.(); break;
      case 'processing': cb.onEnterProcessing?.(); break;
      case 'speaking': cb.onEnterSpeaking?.(); break;
      case 'awaiting_confirmation': cb.onEnterAwaitingConfirmation?.(); break;
      case 'listening_for_confirmation': cb.onEnterListeningForConfirmation?.(); break;
      default: break;
    }
  }, [state]);

  useEffect(() => {
    if (state !== 'error') return undefined;
    const timer = setTimeout(() => {
      setStateRaw((prev) => (prev === 'error' ? 'idle' : prev));
    }, errorAutoResetMs);
    return () => clearTimeout(timer);
  }, [state, errorAutoResetMs]);

  useEffect(() => {
    if (state !== 'processing') return undefined;
    const timer = setTimeout(() => {
      setStateRaw((prev) => {
        if (prev !== 'processing') return prev;
        try { callbacksRef.current.onError?.('timeout'); } catch { /* noop */ }
        logVoiceEvent('voice_state', 'error', 'processing safety timeout',
          { from: 'processing', to: 'error' });
        return 'error';
      });
    }, processingSafetyMs);
    return () => clearTimeout(timer);
  }, [state, processingSafetyMs]);

  const toIdle = useCallback((reason) => transitionTo('idle', reason), [transitionTo]);
  const toRecording = useCallback((reason) => transitionTo('recording', reason), [transitionTo]);
  const toProcessing = useCallback((reason) => transitionTo('processing', reason), [transitionTo]);
  const toSpeaking = useCallback((reason) => transitionTo('speaking', reason), [transitionTo]);
  const toAwaitingConfirmation = useCallback(
    (reason) => transitionTo('awaiting_confirmation', reason),
    [transitionTo],
  );
  const toListeningForConfirmation = useCallback(
    (reason) => transitionTo('listening_for_confirmation', reason),
    [transitionTo],
  );
  const toError = useCallback((reason) => {
    try { callbacksRef.current.onError?.(reason); } catch { /* noop */ }
    return transitionTo('error', reason);
  }, [transitionTo]);

  const isLive = state !== 'idle' && state !== 'error';

  return {
    state,
    setState: setStateRaw,
    transitionTo,
    isLive,
    toIdle,
    toRecording,
    toProcessing,
    toSpeaking,
    toAwaitingConfirmation,
    toListeningForConfirmation,
    toError,
  };
}
